import { useCallback } from 'react';
import { useTranslations } from 'i18n';

const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

export type DayRangeUnit = 'day' | 'week' | 'month' | 'year';

export const pickDayRangeUnit = (days: number): { count: number; unit: DayRangeUnit } => {
    if (days > 0 && days % DAYS_PER_YEAR === 0) {
        return { count: days / DAYS_PER_YEAR, unit: 'year' };
    }
    if (days > 0 && days % DAYS_PER_MONTH === 0) {
        return { count: days / DAYS_PER_MONTH, unit: 'month' };
    }
    if (days > 0 && days % DAYS_PER_WEEK === 0) {
        return { count: days / DAYS_PER_WEEK, unit: 'week' };
    }
    return { count: days, unit: 'day' };
};

const UNIT_KEYS: Record<DayRangeUnit, { one: string; other: string }> = {
    day: { one: 'misc.day', other: 'misc.days' },
    week: { one: 'misc.week', other: 'misc.weeks' },
    month: { one: 'misc.month', other: 'misc.months' },
    year: { one: 'misc.year', other: 'misc.years' },
};

// returns localised string for a given number of days, picking the largest
//  appropriate unit (e.g. 14 days would return "2 weeks")
export const useFormatDayRange = (): ((days: number) => string) => {
    const { __ } = useTranslations();
    return useCallback(
        (days: number) => {
            const { count, unit } = pickDayRangeUnit(days);
            const unitKey = count === 1 ? UNIT_KEYS[unit].one : UNIT_KEYS[unit].other;
            return `${count} ${__(unitKey).toLowerCase()}`;
        },
        [__],
    );
};
